import { useContext, createContext, useState, useEffect, useCallback, useRef } from 'react';
import {
	signInWithPopup,
	signOut,
	onAuthStateChanged,
	GoogleAuthProvider,
} from 'firebase/auth';
import { auth } from '../firebase';
import { getAuthHeaders } from '@/utils/authHeader';
import { PRO_REQUIRED_EVENT } from '@/utils/proRequired';

const AuthContext = createContext();

const BASE_URL = process.env.NEXT_PUBLIC_SERVER_URL;

// The client token's prefix is the source of truth for which Paddle
// environment we talk to. Never default to production.
const getPaddleEnvironment = () => {
	const token = process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN || '';
	const configured = process.env.NEXT_PUBLIC_PADDLE_ENV;
	const fromToken = token.startsWith('test_')
		? 'sandbox'
		: token.startsWith('live_')
			? 'production'
			: null;
	if (fromToken && configured && configured !== fromToken) {
		console.warn(
			`NEXT_PUBLIC_PADDLE_ENV (${configured}) does not match the Paddle client token (${fromToken}); using ${fromToken}.`
		);
	}
	return fromToken || (configured === 'production' ? 'production' : 'sandbox');
};

export const CHECKOUT_UNAVAILABLE_MSG =
	'Upgrades are temporarily unavailable. Please try again later.';

const NETWORK_TIMEOUT_MS = 8000;

// fetch with a timeout. AbortSignal.timeout is missing in older Safari, so
// use an AbortController that works everywhere.
const fetchWithTimeout = async (url, options = {}) => {
	const controller = new AbortController();
	const timer = setTimeout(() => controller.abort(), NETWORK_TIMEOUT_MS);
	try {
		return await fetch(url, { ...options, signal: controller.signal });
	} finally {
		clearTimeout(timer);
	}
};

// Rejects if the promise has not settled within the timeout.
const withTimeout = (promise) =>
	new Promise((resolve, reject) => {
		const timer = setTimeout(
			() => reject(new Error('Timed out')),
			NETWORK_TIMEOUT_MS
		);
		promise.then(
			(value) => {
				clearTimeout(timer);
				resolve(value);
			},
			(err) => {
				clearTimeout(timer);
				reject(err);
			}
		);
	});

// Fail-closed: checkout opens only if the backend can process Paddle webhooks
// (otherwise customers would pay and never become Pro) and runs in the same
// Paddle environment as this client.
const isBillingAvailable = async () => {
	try {
		const res = await fetchWithTimeout(`${BASE_URL}paddle/status`, {
			cache: 'no-store',
		});
		if (!res.ok) return false;
		const status = await res.json();
		return status?.configured === true && status.environment === getPaddleEnvironment();
	} catch (err) {
		console.error('Paddle status check failed:', err);
		return false;
	}
};

// Signed proof (from our backend) of which account is checking out. The
// payment webhook only trusts the account id if this signature checks out.
const getCheckoutBinding = async () => {
	const res = await fetchWithTimeout(`${BASE_URL}subscriptions/checkout-token`, {
		method: 'POST',
		headers: await getAuthHeaders(),
	});
	if (res.status === 409) {
		const body = await res.json().catch(() => ({}));
		const err = new Error(body.msg || 'You already have an active PRO subscription.');
		err.alreadyPro = true;
		throw err;
	}
	if (!res.ok) throw new Error(`checkout-token failed with ${res.status}`);
	const { userId, sig } = await res.json();
	if (!userId || !sig) throw new Error('checkout-token response incomplete');
	return { userId, sig };
};

const UPGRADE_POLL_INTERVAL_MS = 2000;
const UPGRADE_POLL_MAX_MS = 40000;
const CHECKOUT_CLOSE_DELAY_MS = 2000;

export const AuthContextProvider = ({ children }) => {
	const [user, setUser] = useState(null);
	// Our own user record (from the backend); the source of truth for the tier.
	const [dbUser, setDbUser] = useState(null);
	const [userLoading, setUserLoading] = useState(false);
	// uid whose backend record has been fetched; lets us report "loading" from
	// the very first render after sign-in (before the sync effect has run).
	const [syncedUid, setSyncedUid] = useState(null);
	// False until Firebase has told us whether anyone is signed in.
	const [authReady, setAuthReady] = useState(false);
	// Promise of the Paddle.js instance (null if it could not start), so a click
	// before it has finished loading can wait for it instead of failing.
	const paddleInitRef = useRef(null);
	// Progress of an upgrade started by a completed checkout in this session:
	// 'idle' | 'activating' (waiting for the payment webhook) | 'active' (Pro
	// confirmed) | 'delayed' (not Pro within the polling window).
	const [upgradeStatus, setUpgradeStatus] = useState('idle');
	const upgradeDelayed = upgradeStatus === 'delayed';
	const pollRef = useRef(null);
	const dbUserRef = useRef(null);
	dbUserRef.current = dbUser;

	// Fail-closed: pro only when the backend says so; loading/error/signed-out = not pro.
	const isPro = dbUser?.tier === 'pro';

	const stopPolling = useCallback(() => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);

	// Creates/updates the user on the backend and returns it (null on failure).
	const syncUser = useCallback(async () => {
		try {
			const res = await fetch(`${BASE_URL}users/sync`, {
				method: 'POST',
				headers: await getAuthHeaders(),
			});
			if (!res.ok) throw new Error(`users/sync failed with ${res.status}`);
			const data = await res.json();
			return data?.user || null;
		} catch (err) {
			console.error('Failed to sync user:', err);
			return null;
		}
	}, []);

	// Re-reads the user (and tier) from the backend without bumping lastLogin.
	const refreshUser = useCallback(async () => {
		try {
			const res = await fetch(`${BASE_URL}users/me`, {
				cache: 'no-store',
				headers: await getAuthHeaders(),
			});
			if (!res.ok) throw new Error(`users/me failed with ${res.status}`);
			const data = await res.json();
			const fresh = data?.user || null;
			if (fresh) setDbUser(fresh);
			return fresh;
		} catch (err) {
			console.error('Failed to refresh user:', err);
			return null;
		}
	}, []);

	// After a completed checkout the webhook may take a moment; poll until pro.
	const startUpgradePolling = useCallback(() => {
		stopPolling();
		setUpgradeStatus('activating');
		const startedAt = Date.now();
		// Who this poll is for; a refresh that finishes after a logout or user
		// switch must not flag the next user as delayed.
		const startedUid = auth.currentUser?.uid;
		pollRef.current = setInterval(async () => {
			if (Date.now() - startedAt > UPGRADE_POLL_MAX_MS) {
				stopPolling();
				// One last look before telling them it is still pending.
				const last = await refreshUser();
				if (last?.tier !== 'pro' && startedUid && auth.currentUser?.uid === startedUid)
					setUpgradeStatus('delayed');
				return;
			}
			const fresh = await refreshUser();
			if (fresh?.tier === 'pro') stopPolling();
		}, UPGRADE_POLL_INTERVAL_MS);
	}, [refreshUser, stopPolling]);

	useEffect(() => stopPolling, [stopPolling]);

	// Initialize Paddle client-side
	useEffect(() => {
		if (typeof window === 'undefined') return;
		paddleInitRef.current = import('@paddle/paddle-js')
			.then(({ initializePaddle }) =>
				initializePaddle({
					environment: getPaddleEnvironment(),
					token: process.env.NEXT_PUBLIC_PADDLE_CLIENT_TOKEN,
					eventCallback: (event) => {
						if (event?.name === 'checkout.completed') {
							startUpgradePolling();
							// Let the user see Paddle's success tick, then close the overlay
							// ourselves (no successUrl: a page load would kill the polling).
							setTimeout(() => {
								paddleInitRef.current
									?.then((paddle) => paddle?.Checkout?.close())
									.catch((err) => console.error('Could not close checkout:', err));
							}, CHECKOUT_CLOSE_DELAY_MS);
						}
					},
				})
			)
			.then((instance) => instance || null)
			.catch((err) => {
				console.error('Paddle failed to initialise:', err);
				return null;
			});
	}, [startUpgradePolling]);

	// Sync with the backend whenever the signed-in Firebase user changes.
	useEffect(() => {
		if (!user) {
			stopPolling();
			setUpgradeStatus('idle');
			setDbUser(null);
			setUserLoading(false);
			setSyncedUid(null);
			return;
		}
		let cancelled = false;
		setUserLoading(true);
		syncUser().then((synced) => {
			if (cancelled) return;
			setDbUser(synced);
			setUserLoading(false);
			setSyncedUid(user.uid);
		});
		return () => {
			cancelled = true;
		};
	}, [user?.uid, syncUser, stopPolling]); // eslint-disable-line react-hooks/exhaustive-deps

	// Pro confirmed after a checkout in this session (also recovers from
	// 'delayed' if the tier shows up later). Only reachable via a checkout, so
	// a Pro user who merely visits never enters 'active'.
	useEffect(() => {
		if (isPro && (upgradeStatus === 'activating' || upgradeStatus === 'delayed')) {
			stopPolling();
			setUpgradeStatus('active');
		}
	}, [isPro, upgradeStatus, stopPolling]);

	// Called by the page once it has acted on 'active' (e.g. redirected).
	const acknowledgeUpgrade = useCallback(() => setUpgradeStatus('idle'), []);

	// The tier is otherwise only read at sign-in, so re-read it when the tab
	// comes back into view, and when a Pro-only call is refused (a downgrade).
	useEffect(() => {
		if (!user?.uid) return;
		const onVisible = () => {
			if (document.visibilityState === 'visible') refreshUser();
		};
		const onProRequired = () => {
			refreshUser();
		};
		document.addEventListener('visibilitychange', onVisible);
		window.addEventListener(PRO_REQUIRED_EVENT, onProRequired);
		return () => {
			document.removeEventListener('visibilitychange', onVisible);
			window.removeEventListener(PRO_REQUIRED_EVENT, onProRequired);
		};
	}, [user?.uid, refreshUser]);

	// Throws Error(CHECKOUT_UNAVAILABLE_MSG) on any failure so the page can show
	// a friendly message (details go to the console).
	const openCheckout = useCallback(async (priceIdOverride) => {
		// A yearly Paddle plan has not been created yet. Once it exists, pass
		// process.env.NEXT_PUBLIC_PADDLE_PRICE_ID_YEARLY as priceIdOverride; until
		// that env var is set it will be undefined and fall back to the monthly price id.
		const priceId = priceIdOverride || process.env.NEXT_PUBLIC_PADDLE_PRICE_ID;
		if (!priceId || priceId.startsWith('pri_REPLACE')) {
			console.error('Paddle price ID not configured — set NEXT_PUBLIC_PADDLE_PRICE_ID in .env');
			throw new Error(CHECKOUT_UNAVAILABLE_MSG);
		}
		// Firebase's own current user, not the `user` state: right after a Google
		// sign-in the state has not re-rendered yet but this is already set.
		const currentUser = auth.currentUser;
		if (!currentUser) {
			console.warn('User must be logged in to open checkout');
			throw new Error(CHECKOUT_UNAVAILABLE_MSG);
		}
		let paddle = null;
		try {
			// Paddle.js may still be loading (e.g. a fast click on page load).
			paddle = paddleInitRef.current ? await withTimeout(paddleInitRef.current) : null;
		} catch (err) {
			console.error('Paddle did not finish initialising:', err);
		}
		if (!paddle) {
			console.warn('Paddle not initialized — check NEXT_PUBLIC_PADDLE_CLIENT_TOKEN');
			throw new Error(CHECKOUT_UNAVAILABLE_MSG);
		}
		if (!(await isBillingAvailable())) throw new Error(CHECKOUT_UNAVAILABLE_MSG);
		let binding;
		try {
			binding = await getCheckoutBinding();
		} catch (err) {
			if (err.alreadyPro) {
				// Our copy of the tier is stale; re-read it so /pro redirects.
				refreshUser();
				err.fromServer = true;
				throw err;
			}
			console.error('Could not get checkout token:', err);
			throw new Error(CHECKOUT_UNAVAILABLE_MSG);
		}
		try {
			paddle.Checkout.open({
				items: [{ priceId, quantity: 1 }],
				customer: { email: currentUser.email },
				customData: { userId: binding.userId, sig: binding.sig },
			});
		} catch (err) {
			console.error('Paddle checkout failed:', err);
			throw new Error(CHECKOUT_UNAVAILABLE_MSG);
		}
	}, [refreshUser]);

	const openCustomerPortal = useCallback(async () => {
		if (!user) throw new Error('You must be signed in to manage your subscription.');
		const res = await fetch(`${BASE_URL}subscriptions/portal`, {
			method: 'POST',
			headers: await getAuthHeaders(),
		});
		if (!res.ok) {
			const body = await res.json().catch(() => ({}));
			const err = new Error(body.msg || body.error || 'Failed to open the customer portal.');
			// Lets the UI show backend-written text but not raw exception text.
			err.fromServer = Boolean(body.msg || body.error);
			throw err;
		}
		const { url } = await res.json();
		return url;
	}, [user]);

	const googleSignIn = async () => {
		const provider = new GoogleAuthProvider();
		try {
			const result = await signInWithPopup(auth, provider);
			setUser(result.user);
			return result;
		} catch (error) {
			console.error('Error during Google sign-in:', error);
			throw error;
		}
	};

	const logOut = async () => {
		try {
			await signOut(auth);
			setUser(null);
			setDbUser(null);
			stopPolling();
			setUpgradeStatus('idle');
		} catch (error) {
			console.error('Error during sign-out:', error);
		}
	};

	useEffect(() => {
		const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
			setUser(currentUser || null);
			setAuthReady(true);
		});
		return () => unsubscribe();
	}, []);

	return (
		<AuthContext.Provider value={{ user, dbUser, userLoading: userLoading || (Boolean(user) && syncedUid !== user.uid), authReady, isPro, upgradeStatus, upgradeDelayed, acknowledgeUpgrade, refreshUser, openCheckout, openCustomerPortal, googleSignIn, logOut }}>
			{children}
		</AuthContext.Provider>
	);
};

export const UserAuth = () => {
	return useContext(AuthContext);
};
